import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  TablePagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  AdminPanelSettings as AdminIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { fetchRoles } from "store/features/admin/roleManagement/role/roleActions";
import {
  selectRoles,
  selectRoleLoading,
} from "store/features/admin/roleManagement/role/roleSelectors";
import AddRoleModal from "./AddRoleModal";
import rolesSvg from "assets/folders/roles.svg";

const Role = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const roles = useSelector(selectRoles);
  const loading = useSelector(selectRoleLoading);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter, typeFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredRoles = roles.filter((role) => {
    const matchesSearch =
      searchTerm === "" ||
      role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || role.status === statusFilter;

    const matchesType =
      typeFilter === "all" ||
      (typeFilter === "system" && role.is_system === 1) ||
      (typeFilter === "custom" && role.is_system === 0);

    return matchesSearch && matchesStatus && matchesType;
  });

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTenantLabel = (tenantId) => {
    if (tenantId === null) return "Global";
    return `Tenant ${tenantId}`;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // background:
        //   theme.palette.mode === "dark"
        //     ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
        //     : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={rolesSvg}
              alt="Roles"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Role Management
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Manage user roles and permissions
              </Typography>
            </Box>
          </Stack>

          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateModal(true)}
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                borderRadius: "10px",
                px: 2.5,
                py: 1,
                fontSize: "0.875rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg,  #d97706 0%, #b45309 100%))",
                  boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Add Role
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* Search and Filter Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <TextField
          placeholder="Search by name, code or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            maxWidth: { sm: 400 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1}>
          {["all", "active", "inactive"].map((filter) => (
            <Button
              key={filter}
              variant={statusFilter === filter ? "contained" : "outlined"}
              onClick={() => setStatusFilter(filter)}
              sx={{
                textTransform: "capitalize",
                borderRadius: "8px",
                px: 2,
                py: 0.7,
                fontSize: "0.875rem",
                fontWeight: 500,
                ...(statusFilter === filter && {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }),
                ...(statusFilter !== filter && {
                  borderColor:
                    theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                  color: theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                  "&:hover": {
                    borderColor: "#f59e0b",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(245,158,11,0.1)"
                        : "#fffbeb",
                  },
                }),
              }}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </Button>
          ))}
        </Stack>
      </Stack>

      {/* Role Type Filter */}
      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        {["all", "system", "custom"].map((filter) => (
          <Button
            key={filter}
            variant={typeFilter === filter ? "contained" : "outlined"}
            onClick={() => setTypeFilter(filter)}
            size="small"
            sx={{
              textTransform: "capitalize",
              borderRadius: "8px",
              px: 2,
              py: 0.5,
              fontSize: "0.75rem",
              fontWeight: 500,
              ...(typeFilter === filter && {
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
              }),
              ...(typeFilter !== filter && {
                borderColor:
                  theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
              }),
            }}
          >
            {filter === "all"
              ? "All Roles"
              : filter === "system"
                ? "System Roles"
                : "Custom Roles"}
          </Button>
        ))}
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress
              size={48}
              sx={{
                color: "#3b82f6",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              Loading roles...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
              }`,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    ID / Tenant
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  >
                    Role
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  >
                    Details
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  >
                    Priority
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  >
                    Created At
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRoles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <AdminIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        No roles found. Click "Add Role" to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRoles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((role, index) => (
                    <TableRow
                      key={role.id}
                      component={motion.tr}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      sx={{
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.5)"
                              : "#f9fafb",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              fontSize: "0.875rem",
                              color: "primary.main",
                            }}
                          >
                            #{role.id}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: "text.secondary",
                            }}
                          >
                            Tenant: {role.tenant_id}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "12px",

                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <PersonIcon
                              sx={{ fontSize: 28, color: "primary.main" }}
                            />
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                              }}
                            >
                              {role.name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                fontFamily: "monospace",
                                color: "text.secondary",
                              }}
                            >
                              {role.code}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 250 }}>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#4b5563",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {role.description || "No description"}
                          </Typography>
                          <Chip
                            label={getTenantLabel(role.tenant_id)}
                            size="small"
                            sx={{
                              backgroundColor:
                                role.tenant_id === null ? "#dbeafe" : "#fef3c7",
                              color:
                                role.tenant_id === null ? "#1e40af" : "#92400e",
                              fontWeight: 500,
                              fontSize: "0.65rem",
                              height: "20px",
                              width: "fit-content",
                              ...(theme.palette.mode === "dark" && {
                                backgroundColor:
                                  role.tenant_id === null
                                    ? "rgba(59,130,246,0.25)"
                                    : "rgba(245,158,11,0.25)",
                                color:
                                  role.tenant_id === null
                                    ? "#93c5fd"
                                    : "#fde68a",
                              }),
                            }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={
                            role.is_system === 1 ? "System Role" : "Custom Role"
                          }
                          size="small"
                          sx={{
                            backgroundColor:
                              role.is_system === 1 ? "#f3e8ff" : "#d1fae5",
                            color: role.is_system === 1 ? "#6b21a5" : "#065f46",
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            ...(theme.palette.mode === "dark" && {
                              backgroundColor:
                                role.is_system === 1
                                  ? "rgba(139,92,246,0.25)"
                                  : "rgba(16,185,129,0.25)",
                              color:
                                role.is_system === 1 ? "#c4b5fd" : "#6ee7b7",
                            }),
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: "text.primary",
                          }}
                        >
                          {role.priority}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={role.status}
                          size="small"
                          icon={
                            role.status === "active" ? (
                              <CheckCircleIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <CancelIcon sx={{ fontSize: 14 }} />
                            )
                          }
                          sx={{
                            backgroundColor:
                              role.status === "active"
                                ? theme.palette.mode === "dark"
                                  ? "rgba(16,185,129,0.25)"
                                  : "#d1fae5"
                                : theme.palette.mode === "dark"
                                  ? "rgba(239,68,68,0.25)"
                                  : "#fee2e2",
                            color:
                              role.status === "active"
                                ? theme.palette.mode === "dark"
                                  ? "#6ee7b7"
                                  : "#065f46"
                                : theme.palette.mode === "dark"
                                  ? "#fca5a5"
                                  : "#991b1b",
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            "& .MuiChip-icon": {
                              color: "inherit",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack spacing={0.5}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="center"
                          >
                            <EventIcon
                              sx={{ fontSize: 14, color: "primary.main" }}
                            />
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#d1d5db"
                                    : "#4b5563",
                              }}
                            >
                              {formatDateTime(role.created_at)}
                            </Typography>
                          </Stack>
                          {role.updated_at &&
                            role.updated_at !== role.created_at && (
                              <Typography
                                sx={{
                                  fontSize: "0.65rem",
                                  color: "text.secondary",
                                }}
                              >
                                Updated: {formatDateTime(role.updated_at)}
                              </Typography>
                            )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredRoles.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Role Count Summary */}
      {!loading && roles.length > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            mt: 2,
            gap: 2,
            fontSize: "0.875rem",
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">Total Roles: {roles.length}</Typography>
          <Typography variant="body2">
            Showing: {filteredRoles.length} of {roles.length}
          </Typography>
        </Stack>
      )}

      {/* Empty State for No Search Results */}
      {!loading && filteredRoles.length === 0 && roles.length > 0 && (
        <Paper
          sx={{
            mt: 4,
            p: 4,
            textAlign: "center",
            borderRadius: "16px",
            bgcolor: "background.paper",
          }}
        >
          <SearchIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
          <Typography
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            No roles match your search criteria
          </Typography>
          <Button
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
              setTypeFilter("all");
            }}
            sx={{
              color: "#3b82f6",
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(59,130,246,0.1)"
                    : "#eff6ff",
              },
            }}
          >
            Clear filters
          </Button>
        </Paper>
      )}

      {/* Add Role Modal */}
      <AddRoleModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => dispatch(fetchRoles())}
      />
    </Box>
  );
};

export default Role;
